import React from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import type { BreakEvenData, BreakEvenScenario, CostItem, SensitivityPoint } from './use-finance-break-even.js';

function fmt(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function pct(n: number): string { return `${(n * 100).toFixed(1)}%`; }

interface Props { data: BreakEvenData; onRefresh: () => void; }

export default function FinanceBreakEven({ data, onRefresh }: Props) {
  const { current, scenarios, sensitivity, cost_breakdown, period } = data;

  if (!current) {
    return <div style={{ color: C.textMuted, fontSize: 13, padding: 20, textAlign: 'center' }}>No break-even data for this period.</div>;
  }

  const aboveBe = current.actual_vs_break_even >= 1;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: aboveBe ? C.green : C.red }}>
            {current.actual_vs_break_even.toFixed(2)}x
          </div>
          <div style={{ fontSize: 12, color: C.textMuted }}>
            {aboveBe ? 'Above' : 'Below'} break-even \u00b7 {period.weeks_included} week{period.weeks_included !== 1 ? 's' : ''}
          </div>
        </div>
        <button onClick={onRefresh} style={{ background: 'none', border: `1px solid ${C.borderSoft}`, borderRadius: 6, color: C.textMuted, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>Refresh</button>
      </div>

      {/* Key metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
        {[
          { label: 'Revenue', value: fmt(current.revenue), color: C.green },
          { label: 'Fixed Costs', value: fmt(current.fixed_costs), color: C.text },
          { label: 'Variable Costs', value: fmt(current.variable_costs), color: C.text },
          { label: 'Contribution Margin', value: pct(current.contribution_margin), color: current.contribution_margin > 0 ? C.green : C.red },
          { label: 'Break-Even Revenue', value: fmt(current.break_even_revenue), color: C.gold },
        ].map(s => (
          <div key={s.label} style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}`, textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase', marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Scenarios */}
      {scenarios.length > 0 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>Scenarios</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>{['Scenario', 'Revenue', 'Fixed', 'Variable', 'Net'].map(h => (
                <th key={h} style={{ padding: '6px 8px', textAlign: h === 'Scenario' ? 'left' : 'right', color: C.textMuted, fontWeight: 500, fontSize: 10, textTransform: 'uppercase', borderBottom: `1px solid ${C.borderSoft}` }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {scenarios.map((s: BreakEvenScenario) => (
                <tr key={s.name} style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                  <td style={{ padding: '8px', color: C.text }}>{s.name}</td>
                  <td style={{ padding: '8px', color: C.green, textAlign: 'right' }}>{fmt(s.revenue)}</td>
                  <td style={{ padding: '8px', color: C.textMuted, textAlign: 'right' }}>{fmt(s.fixed)}</td>
                  <td style={{ padding: '8px', color: C.textMuted, textAlign: 'right' }}>{fmt(s.variable)}</td>
                  <td style={{ padding: '8px', color: s.net >= 0 ? C.green : C.red, textAlign: 'right', fontWeight: 600 }}>{fmt(s.net)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Sensitivity */}
      {sensitivity.length > 0 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>Revenue sensitivity</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {sensitivity.map((pt: SensitivityPoint) => (
              <div key={pt.revenue_change_pct} style={{ padding: '8px 12px', borderRadius: 8, background: C.panel, border: `1px solid ${C.borderSoft}`, textAlign: 'center', minWidth: 70 }}>
                <div style={{ fontSize: 11, color: pt.revenue_change_pct >= 0 ? C.green : C.red, fontWeight: 600 }}>
                  {pt.revenue_change_pct > 0 ? '+' : ''}{pt.revenue_change_pct}%
                </div>
                <div style={{ fontSize: 12, color: pt.net >= 0 ? C.green : C.red, marginTop: 2 }}>{fmt(pt.net)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cost breakdown */}
      {(cost_breakdown.fixed.length > 0 || cost_breakdown.variable.length > 0) && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>Cost breakdown</div>
          {cost_breakdown.fixed.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: C.textMuted, textTransform: 'uppercase', marginBottom: 6 }}>Fixed ({fmt(cost_breakdown.fixed.reduce((s, c) => s + c.amount, 0))})</div>
              {cost_breakdown.fixed.map((c: CostItem, i: number) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12, borderBottom: `1px solid ${C.borderSoft}` }}>
                  <span style={{ color: C.text }}>{c.label}</span>
                  <span style={{ color: C.textMuted }}>{fmt(c.amount)}</span>
                </div>
              ))}
            </div>
          )}
          {cost_breakdown.variable.length > 0 && (
            <div>
              <div style={{ fontSize: 11, color: C.textMuted, textTransform: 'uppercase', marginBottom: 6 }}>Variable ({fmt(cost_breakdown.variable.reduce((s, c) => s + c.amount, 0))})</div>
              {cost_breakdown.variable.slice(0, 15).map((c: CostItem, i: number) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12, borderBottom: `1px solid ${C.borderSoft}` }}>
                  <span style={{ color: C.text }}>{c.label}</span>
                  <span style={{ color: C.textMuted }}>{fmt(c.amount)}</span>
                </div>
              ))}
              {cost_breakdown.variable.length > 15 && (
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6 }}>+ {cost_breakdown.variable.length - 15} more</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
